import { matchPath } from 'react-router';
import { OpenMenu } from '../../../types/types';
import { getDatasetUrlParams } from '../../../utils/getDatasetUrlParams';
import { getMenuLeafKey } from './getMenuLeafKey';

export type ActiveMenu = {
  // [blockKey, submenuKey] of the accordion holding the active item.
  openMenu: OpenMenu;
  // data-menu-url of the active item (or null when nothing matches).
  selectedItem: string | null;
};

type RoutesLike = {
  datasetList: (url: string) => string;
  relative: { formAddAction: () => string; application: () => string };
};

// Resolves which menu block/submenu + item the current route belongs to. Pure, so useActiveMenu can run
// it during render and have openMenu correct on the SAME render the route changes (no lag → the active
// block never momentarily collapses, which previously desynced Ionic's accordion).
export const computeActiveMenu = (
  menuItems: any[],
  matchSource: string,
  routes: RoutesLike,
  peersets?: any[]
): ActiveMenu => {
  let openMenu: OpenMenu = [undefined, undefined];
  let selectedItem: string | null = null;

  const { datasetId } = getDatasetUrlParams(matchSource);
  // Built through routes.* so the deployment's subUrl prefix is on both sides of the compare.
  const currentUrlToCompare: string | null = datasetId
    ? routes.datasetList(datasetId)
    : null;
  // Standalone form route (/form/:formId/add[/reference/...]) — match the leaf by formId.
  const formId = matchPath<{ formId: string }>(matchSource, {
    path: routes.relative.formAddAction(),
  })?.params.formId;
  const appId = matchPath<{ appId: string }>(matchSource, {
    path: routes.relative.application(),
  })?.params.appId;

  // Match by URL (or standalone form id). Stop at the first match (return true) so the FIRST
  // matching block/item wins, not the last.
  menuItems.some((block) =>
    block.children.some((item) => {
      if (item.children) {
        const submenuOpen = item.children.find(
          (subItem) =>
            currentUrlToCompare === routes.datasetList(subItem.url) ||
            matchSource === routes.datasetList(subItem.url) ||
            (!!subItem.formId && subItem.formId === formId) ||
            (!!subItem.appId && subItem.appId === appId)
        );
        if (submenuOpen) {
          selectedItem = getMenuLeafKey(submenuOpen) ?? null;
          openMenu = [block.key, item.key];
          return true;
        }
      } else if (
        currentUrlToCompare === routes.datasetList(item.url) ||
        (!!item.formId && item.formId === formId) ||
        (!!item.appId && item.appId === appId)
      ) {
        selectedItem = getMenuLeafKey(item) ?? null;
        openMenu = [block.key, undefined];
        return true;
      }
      return false;
    })
  );

  // Fallback: no URL match, but the dataset is in the current peersets.
  if (openMenu[0] === undefined && peersets) {
    menuItems.some((block) =>
      block.children.some((item) => {
        const inPeersets = (url: string) =>
          peersets.some((peerset) =>
            peerset.peersets
              ? peerset.peersets.some(
                  (subPeerset: any) => url === subPeerset.datasetId
                )
              : url === peerset.datasetId
          );

        if (item.children) {
          const submenuOpen = item.children.find((subItem) =>
            inPeersets(subItem.url)
          );
          if (submenuOpen) {
            selectedItem = submenuOpen.url;
            openMenu = [block.key, item.key];
            return true;
          }
        } else if (inPeersets(item.url)) {
          selectedItem = item.url;
          openMenu = [block.key, undefined];
          return true;
        }
        return false;
      })
    );
  }

  return { openMenu, selectedItem };
};
